export const IYZICO_CHECKOUT_FORM_ID = 'iyzipay-checkout-form'

const PROVIDER_LOAD_TIMEOUT_MS = 15000

let currentAttempt = null
let nextAttemptId = 0

function clearHost(host) {
  if (!host) return
  while (host.firstChild) host.removeChild(host.firstChild)
}

function resetIyzicoBootstrap(windowRef) {
  if (windowRef) Reflect.set(windowRef, 'iyziInit', undefined)
}

function disposeAttempt(attempt) {
  attempt.cancelTimeout(attempt.bootstrapTimeoutId)
  attempt.deferredTimers.forEach(cancel => cancel())
  attempt.deferredTimers.clear()
  attempt.providerScripts.forEach((state, script) => {
    const providerScript = script
    providerScript.removeEventListener('load', state.onLoad)
    providerScript.removeEventListener('error', state.onError)
    attempt.cancelTimeout(state.timeoutId)
    providerScript.onload = null
    providerScript.onerror = null
    providerScript.remove()
  })
  attempt.providerScripts.clear()
  attempt.pendingProviderScripts.clear()

  if (currentAttempt === attempt) {
    currentAttempt = null
    resetIyzicoBootstrap(attempt.windowRef)
  }
}

function settleAttempt(attempt, mounted) {
  const current = attempt
  if (current.settled) return
  current.settled = true
  current.active = false
  current.cancelTimeout(current.bootstrapTimeoutId)
  if (!mounted) {
    clearHost(current.host)
    disposeAttempt(current)
  }
  current.resolve(mounted)
}

function maybeSettleMounted(attempt) {
  if (!attempt.active
    || attempt.bootstrapping
    || !attempt.providerLoadSucceeded
    || attempt.deferredTimers.size > 0
    || attempt.pendingProviderScripts.size > 0) return
  settleAttempt(attempt, true)
}

function trackProviderScript(attempt, script) {
  if (attempt.providerScripts.has(script)) return

  const current = attempt
  const providerScript = script
  providerScript.dataset.iyzicoCheckoutLoader = String(attempt.id)
  const finishLoad = () => {
    const state = attempt.providerScripts.get(providerScript)
    if (!state) return
    attempt.cancelTimeout(state.timeoutId)
    providerScript.removeEventListener('load', state.onLoad)
    providerScript.removeEventListener('error', state.onError)
    attempt.pendingProviderScripts.delete(providerScript)
    current.providerLoadSucceeded = true
    Promise.resolve().then(() => maybeSettleMounted(attempt))
  }
  const failLoad = () => settleAttempt(attempt, false)
  const state = {
    onLoad: finishLoad,
    onError: failLoad,
    timeoutId: attempt.scheduleTimeout(failLoad, PROVIDER_LOAD_TIMEOUT_MS),
  }
  attempt.providerScripts.set(providerScript, state)
  attempt.pendingProviderScripts.add(providerScript)
  providerScript.addEventListener('load', state.onLoad, { once: true })
  providerScript.addEventListener('error', state.onError, { once: true })
}

function withBootstrapOwnership(attempt, callback) {
  if (!attempt.active || currentAttempt !== attempt) return undefined

  const {
    documentRef, windowRef, ownedScripts,
  } = attempt
  const originalCreateElement = documentRef.createElement
  const originalHeadAppendChild = documentRef.head && documentRef.head.appendChild
  const originalBodyAppendChild = documentRef.body && documentRef.body.appendChild
  const originalSetTimeout = windowRef.setTimeout
  const originalClearTimeout = windowRef.clearTimeout

  documentRef.createElement = function createOwnedElement(tagName, options) {
    const node = originalCreateElement.call(this, tagName, options)
    if (String(tagName).toUpperCase() === 'SCRIPT') ownedScripts.add(node)
    return node
  }

  const interceptOwnedAppend = (parent, originalAppendChild) => {
    if (!parent || !originalAppendChild) return
    const scriptParent = parent
    scriptParent.appendChild = function appendOwnedProviderScript(node) {
      if (node
        && node.tagName === 'SCRIPT'
        && node.src
        && ownedScripts.has(node)) {
        trackProviderScript(attempt, node)
      }
      return originalAppendChild.call(this, node)
    }
  }
  interceptOwnedAppend(documentRef.head, originalHeadAppendChild)
  interceptOwnedAppend(documentRef.body, originalBodyAppendChild)

  windowRef.setTimeout = function scheduleOwnedBootstrap(callbackFn, delay, ...args) {
    if (typeof callbackFn !== 'function') {
      return originalSetTimeout.call(this, callbackFn, delay, ...args)
    }

    let timerId
    const runOwnedCallback = (...callbackArgs) => {
      attempt.deferredTimers.delete(timerId)
      if (!attempt.active || currentAttempt !== attempt) return undefined
      try {
        return withBootstrapOwnership(
          attempt,
          () => callbackFn.apply(windowRef, callbackArgs),
        )
      } catch {
        settleAttempt(attempt, false)
        return undefined
      } finally {
        Promise.resolve().then(() => maybeSettleMounted(attempt))
      }
    }
    timerId = originalSetTimeout.call(this, runOwnedCallback, delay, ...args)
    attempt.deferredTimers.set(
      timerId,
      () => originalClearTimeout.call(windowRef, timerId),
    )
    return timerId
  }
  windowRef.clearTimeout = function clearOwnedBootstrapTimeout(timerId) {
    attempt.deferredTimers.delete(timerId)
    return originalClearTimeout.call(this, timerId)
  }

  try {
    return callback()
  } finally {
    documentRef.createElement = originalCreateElement
    if (documentRef.head) documentRef.head.appendChild = originalHeadAppendChild
    if (documentRef.body) documentRef.body.appendChild = originalBodyAppendChild
    windowRef.setTimeout = originalSetTimeout
    windowRef.clearTimeout = originalClearTimeout
  }
}

function retireCurrentAttempt() {
  const attempt = currentAttempt
  if (!attempt) return

  clearHost(attempt.host)
  if (attempt.settled) {
    disposeAttempt(attempt)
  } else {
    settleAttempt(attempt, false)
  }
}

export function cleanupIyzicoCheckout(host) {
  clearHost(host)

  const attempt = currentAttempt
  if (!attempt || attempt.host !== host) return

  if (attempt.settled) {
    disposeAttempt(attempt)
  } else {
    settleAttempt(attempt, false)
  }
}

export async function mountIyzicoCheckout(
  host,
  checkoutFormContent,
  documentRef = document,
  windowRef = window,
) {
  if (!host || !checkoutFormContent) return false

  retireCurrentAttempt()
  nextAttemptId += 1

  clearHost(host)
  resetIyzicoBootstrap(windowRef)

  let resolveAttempt
  const scheduleTimeout = windowRef.setTimeout.bind(windowRef)
  const cancelTimeout = windowRef.clearTimeout.bind(windowRef)
  const attempt = {
    id: nextAttemptId,
    active: true,
    bootstrapping: true,
    bootstrapTimeoutId: null,
    cancelTimeout,
    deferredTimers: new Map(),
    documentRef,
    done: new Promise(resolve => { resolveAttempt = resolve }),
    host,
    ownedScripts: new WeakSet(),
    pendingProviderScripts: new Set(),
    providerScripts: new Map(),
    providerLoadSucceeded: false,
    resolve: null,
    scheduleTimeout,
    settled: false,
    windowRef,
  }
  attempt.resolve = resolveAttempt
  currentAttempt = attempt

  const checkoutTarget = documentRef.createElement('div')
  checkoutTarget.id = IYZICO_CHECKOUT_FORM_ID
  checkoutTarget.className = 'responsive'
  host.appendChild(checkoutTarget)

  const parsedContent = documentRef.createElement('div')
  parsedContent.innerHTML = checkoutFormContent

  attempt.bootstrapTimeoutId = attempt.scheduleTimeout(
    () => settleAttempt(attempt, false),
    PROVIDER_LOAD_TIMEOUT_MS,
  )
  try {
    Array.from(parsedContent.childNodes).forEach(node => {
      if (node.tagName === 'SCRIPT') {
        const script = documentRef.createElement('script')
        if (node.src) {
          script.src = node.src
          trackProviderScript(attempt, script)
        } else {
          script.textContent = node.textContent
        }
        script.type = node.type || 'text/javascript'
        withBootstrapOwnership(attempt, () => host.appendChild(script))
        return
      }

      if (node.id !== IYZICO_CHECKOUT_FORM_ID) host.appendChild(node)
    })
  } catch {
    settleAttempt(attempt, false)
  }

  attempt.bootstrapping = false
  maybeSettleMounted(attempt)
  return attempt.done
}
